import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { FaShareAlt, FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import KafalaOrphanAvatar, { displayOrphelinName, formatOrphelinDate } from '../components/KafalaOrphanAvatar';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { whatsappHrefWithText } from '../utils/contactLinks';
import { getPublicKafalaOrphelin } from '../services/kafalaService';
import { usePublicPaths } from '../routes/publicPaths';
import '../components/KafalaOrphelinsWaiting.css';
import './KafalaOrphelinPublic.css';

const KafalaOrphelinPublic = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const paths = usePublicPaths();
  const settings = useSiteSettings();
  const [orphelin, setOrphelin] = useState(null);
  const [loading, setLoading] = useState(true);
  const whatsappNumber = settings.whatsapp || settings.phone || '';

  useEffect(() => {
    setLoading(true);
    getPublicKafalaOrphelin(id)
      .then(setOrphelin)
      .catch(() => setOrphelin(null))
      .finally(() => setLoading(false));
  }, [id]);

  const name = displayOrphelinName(orphelin || {});
  const waHref = orphelin
    ? whatsappHrefWithText(whatsappNumber, `أريد كفالة هدا اليتيم : ${name}`)
    : null;
  const listPath = `${paths.programme('kafala')}#waiting`;

  const share = async () => {
    const url = window.location.href;
    const title = `كفالة ${name}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore
    }
  };

  return (
    <div className="public-simple-page kafala-orphan-public">
      <Helmet>
        <title>{name ? `${name} | ${t('public.brand.titleSuffix')}` : `${t('kafalaPublic.detailTitle')} | ${t('public.brand.titleSuffix')}`}</title>
      </Helmet>
      <Navbar />
      <main className="public-simple-page__main">
        <Link to={listPath} className="public-simple-page__muted">
          {t('kafalaPublic.backToList')}
        </Link>
        {loading ? (
          <p className="public-simple-page__muted">…</p>
        ) : !orphelin ? (
          <p className="public-simple-page__muted">{t('kafalaPublic.notFound')}</p>
        ) : (
          <article className="kafala-orphan-public__card">
            <header className="kafala-orphan-public__head">
              <h1>{name}</h1>
              <button type="button" className="kafala-orphan-card__share" onClick={share} aria-label={t('kafalaPublic.share')}>
                <FaShareAlt />
              </button>
            </header>
            <KafalaOrphanAvatar orphelin={orphelin} className="kafala-orphan-public__photo" />
            <dl className="kafala-orphan-card__meta kafala-orphan-public__meta">
              <div>
                <dt>{t('kafalaPublic.birthDate')}</dt>
                <dd>{formatOrphelinDate(orphelin.date_naissance)}</dd>
              </div>
              <div>
                <dt>{t('kafalaPublic.city')}</dt>
                <dd>{orphelin.ville || t('kafalaPublic.unknownCity')}</dd>
              </div>
              <div>
                <dt>{t('kafalaPublic.orphanSince')}</dt>
                <dd>{formatOrphelinDate(orphelin.date_inscription)}</dd>
              </div>
            </dl>
            <div className="kafala-orphan-public__actions">
              {waHref ? (
                <a className="public-simple-btn public-simple-btn--wa" href={waHref} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp /> {t('kafalaPublic.whatsappCta')}
                </a>
              ) : (
                <p className="public-simple-page__muted">{t('kafalaPublic.whatsappMissing')}</p>
              )}
              <Link className="public-simple-btn public-simple-btn--ghost" to={listPath}>
                {t('kafalaPublic.backToList')}
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
};

export default KafalaOrphelinPublic;
